import React, { useState, useEffect, useContext } from "react"
import { Link, graphql, useStaticQuery } from "gatsby"

import Image from "./Image"
import Search from "./Search"
import { AppContext } from "./Context"

import styles from "./header.module.scss"
import { toTitleCase } from "../helpers"


const Header = ({ location, navigate, navTheme }) => {

  const data = useStaticQuery(graphql`
    query HeaderQuery {
      contentfulMainHeader(componentName: {eq: "Site Header"}) {
        logo {
          ...LinkFragment
        }
        discoveryLinks {
          ...LinkFragment
          ... on ContentfulLinkGroup {
            heading {
              ...HeadingFragment
            }
            links {
              ...LinkFragment
            }
          }
        }
        accountLinks {
          ...LinkFragment
        }
      }
    }
  `)

  const appState = useContext(AppContext)

  const [navReduced, setNavReduced] = useState(false)
  const [searchFocused, setSearchFocused] = useState(false)

  const {
    logo,
    discoveryLinks,
    accountLinks,
  } = data.contentfulMainHeader

  const colorSchemeNav = toTitleCase(navTheme || "light")

  //Handle reduced format header on scroll
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll, true)
    }
    return (
      window.removeEventListener("scroll", handleScroll)
    )
  })

  const handleScroll = e => {
    if (window.outerWidth >= 1024 && location.pathname !== "/")
      (window.scrollY > 250) ?
        setNavReduced(true)
        :
        setNavReduced(false)
  }

  //Handle hover for subnav links on desktop
  let linksCloseTimeout

  const handleLinksEnter = e => {
    if (window.outerWidth >= 1024) {
      clearTimeout(linksCloseTimeout)
      if(e.target.textContent === "Shoes")
        appState.set({subnavOpen: true})
    }
  }

  const handleLinksLeave = e => {
    if (window.outerWidth >= 1024) {
      linksCloseTimeout = setTimeout(() => {
        appState.set({subnavOpen: false})
      }, 1000)
    } else {
      appState.set({ navOpen: false, subnavOpen: false })
    }
  }

  return (
    <header
      className={[
        (appState.navOpen) ? styles.headerNavOpen : styles.header,
        (navReduced) ? styles.headerNavReduced : "",
        (appState.subnavOpen) ? styles.headerSubNavOpen : "",
        styles[`navTheme${colorSchemeNav}`],
      ].join(" ")}
    >
      <div className={styles.headerWrapper}>
        <Link
          to={logo.url}
          className={styles.logo}
        >
          <Image
            image={logo.image}
            altText={logo.text}
          />
        </Link>

        {!location.pathname.includes("/cart") &&
          <>
            <nav
              className={(searchFocused) ? styles.navSearchFocused : styles.nav}
            >

              {/* Mobile search field */}
              <form
                action=""
                className={styles.searchForm}
                autoComplete="off"
                onSubmit={e => {
                  e.preventDefault()
                  navigate(`/search?query=${encodeURIComponent(appState.searchTerm)}`)
                  appState.set({ navOpen: !appState.navOpen })
                  document.activeElement.blur()
                }}
              >
                <input
                  type="submit"
                  className={styles.searchSubmit}
                />
                <input
                  type="search"
                  placeholder="Search"
                  className={styles.searchInput}
                  value={appState.searchTerm}
                  onChange={e => {
                    appState.set({ searchTerm: e.currentTarget.value })
                  }}
                  onFocus={e => setSearchFocused(true)}
                  onBlur={e => setSearchFocused(false)}
                />
              </form>

              <div
                role="presentation"
                onMouseLeave={handleLinksLeave}
              >
                <ul
                  className={styles.linksDiscovery}
                >
                  {discoveryLinks.map((link, i) =>
                    <li
                      key={i}
                      className={styles.linkItem}
                    >
                      {link.links ?
                        <>
                          <button
                            className={`${styles.link} ${styles.btnHoverOnly}`}
                            onClick={e => appState.set({subnavOpen: !appState.subnavOpen})}
                            onMouseEnter={handleLinksEnter}
                          >
                            {link.heading.heading}
                          </button>
                          <div
                            role="presentation"
                            onMouseEnter={handleLinksEnter}
                          >
                            <ul
                              className={(appState.subnavOpen) ? styles.linksSubOpen : styles.linksSub}
                            >
                              {link.links.map((subLink, j) =>
                                <li key={j} className={styles.linkItem}>
                                  <Link
                                    to={`${subLink.url}?origin=nav`}
                                    className={`${styles.link} ${styles[`link${subLink.text.replace(/\s/g, "")}`]}`}
                                    onClick={handleLinksLeave}
                                  >
                                    {subLink.image ?
                                      <Image
                                        image={subLink.image}
                                        altText={subLink.text}
                                      />
                                      :
                                      subLink.text
                                    }
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </div>
                        </>
                        :
                        <Link
                          to={`${link.url}?origin=nav`}
                          className={`${styles.link} ${styles[`link${link.text.replace(/\s/g, "")}`]}`}
                          onMouseEnter={handleLinksEnter}
                          onClick={e => appState.set({ navOpen: false })}
                        >
                          {link.image ?
                            <Image
                              image={link.image}
                              altText={link.text}
                            />
                            :
                            link.text
                          }
                        </Link>
                      }
                    </li>
                  )}
                </ul>
              </div>

              <ul className={styles.linksAccount}>
                {accountLinks.map((link, i) =>
                  <li
                    key={i}
                    className={styles.linkItem}
                  >
                    {link.url.includes("http") ?
                      <a
                        href={link.url}
                        className={`${styles.link} ${styles[`link${link.text.replace(/\s/g, "")}`]}`}
                        onClick={e => appState.set({ navOpen: false })}
                      >
                        {link.image ?
                          <Image
                            image={link.image}
                            altText={link.text}
                          />
                          :
                          link.text
                        }
                      </a>
                      :
                      <Link
                        to={link.url}
                        className={`${styles.link} ${styles[`link${link.text.replace(/\s/g, "")}`]}`}
                        onClick={e => appState.set({ navOpen: false })}
                      >
                        {link.image ?
                          <Image
                            image={link.image}
                            altText={link.text}
                          />
                          :
                          link.text
                        }
                      </Link>
                    }
                  </li>
                )}
              </ul>
            </nav>

            <ul
              className={styles.linksMisc}
            >
              <li className={styles.linkItemSearch}>
                {/*<button type="button">Search</button>*/}
                {/*<Link to="/search">Search</Link>*/}
                <div className={styles.searchContainer}>
                  <Search
                    location={location}
                    navigate={navigate}
                  />
                </div>
              </li>
              <li className={styles.linkItemCart}>
                <Link to="/cart?origin=nav" title="Cart">
                  <span className={styles.count}>{appState.cartCount}</span>
                </Link>
              </li>
              <li className={styles.linkItemMenu}>
                <button
                  className={(appState.navOpen) ? styles.linkClose : styles.linkMenu}
                  onClick={e => appState.set({ navOpen: !appState.navOpen })}
                >
                  Menu
                </button>
              </li>
            </ul>
          </>
        }
      </div>
    </header>
  )
}

export default Header

export const query = graphql`
  fragment LinkFragment on ContentfulLink {
    text
    url
    image {
      file {
        url
        contentType
      }
    }
  }
`
